import React from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { StudioProvider } from './contexts/StudioContext';
import { ToastProvider } from './contexts/ToastContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { Login } from './components/Login';
import { AdminDashboard } from './components/AdminDashboard';
import Studio from './Studio';
import { LandingPage } from './components/LandingPage';
import { Loader2 } from 'lucide-react';

// Main App Content Component
const AppContent: React.FC = () => {
  const { user, isBetaTester, loading, isAdmin } = useAuth();
  const [showLogin, setShowLogin] = React.useState(false);

  // Check for admin route
  const isAdminRoute = window.location.pathname === '/admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  // Authenticated User
  if (user && isBetaTester) {
    if (isAdminRoute && isAdmin) {
      return <AdminDashboard />;
    }
    // Wrap Studio with StudioProvider for centralized state management
    return (
      <ProjectProvider>
        <StudioProvider>
          <Studio />
        </StudioProvider>
      </ProjectProvider>
    );
  }

  // Unauthenticated: Show Login only if explicitly requested or navigating to a protected route (simplified here)
  if (showLogin) {
    // Pass a way to go back to Landing Page if needed, or rely on browser back if we used real routing. 
    // Since <Login /> likely doesn't have a "Back" button prop, we wrap it or just show it.
    // For better UX, let's wrap it in a container that allows going back? 
    // Or just render Login. Login usually leads to Auth.
    return (
      <div className="relative">
        <button
          onClick={() => setShowLogin(false)}
          className="absolute top-4 left-4 z-50 text-white/50 hover:text-white px-4 py-2"
        >
          ← Retour
        </button>
        <Login />
      </div>
    );
  }

  // Default: Landing Page
  // We need to import LandingPage first. I will add the import at the top of the file in a separate edit if needed, 
  // but let's assume I can add it here or use MultiReplace. 
  // Wait, I cannot add the import with this tool usage effortlessly if I don't select the top.
  // I will use MultiReplace to add the import AND change this component.
  return <LandingPage onLoginClick={() => setShowLogin(true)} />;
};

// Root App Component
function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
